import { createContext, useContext, useState, useEffect, useMemo, type Dispatch, type SetStateAction, type ReactNode } from 'react';
import { db } from '../config/data/firebase';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { useAuth } from './AuthContext';

interface EmailRecord {
	isRead?: boolean;
	[key: string]: unknown;
}

export interface MailboxContextValue {
	emails: EmailRecord[];
	loading: boolean;
	permittedFolders: string[];
	permittedAliases: string[];
	selectedFolderId: string;
	setSelectedFolderId: Dispatch<SetStateAction<string>>;
	selectedAliasFilter: string;
	setSelectedAliasFilter: Dispatch<SetStateAction<string>>;
	member: ReturnType<typeof useAuth>['member'];
	unreadCount: number;
	refreshMailbox: () => void;
}

const MailboxContext = createContext<MailboxContextValue | undefined>(undefined);

export const MailboxProvider = ({ children }: { children: ReactNode }) => {
	const [emails, setEmails] = useState<EmailRecord[]>([]);
	const [permittedFolders, setPermittedFolders] = useState<string[]>([]);
	const [permittedAliases, setPermittedAliases] = useState<string[]>([]);
	const [selectedFolderId, setSelectedFolderId] = useState('inbox');
	const [selectedAliasFilter, setSelectedAliasFilter] = useState('all');
	const [loading, setLoading] = useState(true);
	const { member } = useAuth();

	useEffect(() => {
		if (member?.permissions?.email) {
			const folders = Object.keys(member.permissions.emails?.folders || {}).filter(
				(f) => member.permissions.emails?.folders?.[f]
			);
			const groupAliases = Object.keys(member.permissions.emails?.aliases || {}).filter(
				(a) => member.permissions.emails?.aliases?.[a]
			);
			const alias = (member as Record<string, unknown>).alias as string | undefined;
			const personalAlias = alias ? alias.split('@')[0].toLowerCase() : null;

			const allAliases = [...groupAliases];
			if (personalAlias && !allAliases.includes(personalAlias)) {
				allAliases.push(personalAlias);
			}
			const sortedAliases = [...allAliases].sort((a, b) => a.localeCompare(b));

			setPermittedFolders((current) => (JSON.stringify(current) === JSON.stringify(folders) ? current : folders));
			setPermittedAliases((current) => (JSON.stringify(current) === JSON.stringify(sortedAliases) ? current : sortedAliases));

			if (!selectedFolderId || !folders.includes(selectedFolderId)) {
				setSelectedFolderId(folders.includes('inbox') ? 'inbox' : folders[0] || '');
			}
		} else {
			setPermittedFolders([]);
			setPermittedAliases([]);
			setEmails([]);
			setLoading(false);
		}
	}, [member, selectedFolderId]);

	useEffect(() => {
		if (!member?.permissions?.email || permittedAliases.length === 0 || !selectedFolderId) {
			setLoading(false);
			setEmails([]);
			return () => {};
		}

		setLoading(true);

		const q = query(
			collection(db, 'mail_cache'),
			where('folderName', '==', selectedFolderId),
			where('tags', 'array-contains-any', permittedAliases),
			orderBy('timestamp', 'desc')
		);

		const unsubscribe = onSnapshot(
			q,
			(querySnapshot) => {
				const emailsData: EmailRecord[] = [];
				for (const doc of querySnapshot.docs) {
					emailsData.push(doc.data() as EmailRecord);
				}
				setEmails(emailsData);
				setLoading(false);
			},
			(error) => {
				console.error('MailboxContext: Error listening to mail_cache:', error);
				setLoading(false);
			}
		);

		return () => unsubscribe();
	}, [member, permittedAliases, selectedFolderId]);

	const value = useMemo<MailboxContextValue>(
		() => ({
			emails,
			loading,
			permittedFolders,
			permittedAliases,
			selectedFolderId,
			setSelectedFolderId,
			selectedAliasFilter,
			setSelectedAliasFilter,
			member,
			unreadCount: emails.filter((e) => !e.isRead).length,
			refreshMailbox: () => {},
		}),
		[emails, loading, permittedFolders, permittedAliases, selectedFolderId, selectedAliasFilter, member]
	);

	return <MailboxContext.Provider value={value}>{children}</MailboxContext.Provider>;
};

export const useMailbox = (): MailboxContextValue => {
	const context = useContext(MailboxContext);
	if (context === undefined) {
		throw new Error('useMailbox must be used within a MailboxProvider');
	}
	return context;
};
